'use client';

import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tx, useCurrentUser } from '@akademi/site-builder-runtime';
import type { FormDefinition } from '@akademi/form-designer/schema';
import { sectionInner } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { DynamicFormRenderer, type ValidationLabels } from './DynamicFormRenderer';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Drops any designer-authored form onto any page.
 *
 * <p>The operator picks a form in the page designer (by slug — the same key
 * the Form Designer shows on the form); this block fetches that form's
 * document at render time and renders it. Nothing about the form is copied
 * into the page: editing the form in the designer changes every page that
 * embeds it, and a page can embed several different forms.
 *
 * <p>Data path — {@code GET /api/forms/{form}} and
 * {@code POST /api/forms/{form}/submit}, portal routes that proxy the public
 * tenant-scoped backend surface
 * ({@code /v1/public/{tenantId}/dynamic/forms/...}). The submit stores one
 * {@code DynamicFormEntry}; a form marked "one per person" reports back that
 * the viewer already filled it, and the block renders the submitted state
 * instead of the fields.
 */
export interface DynamicFormBlockProps {
  /** Which form to render — its slug (or numeric id). Required. */
  form?: string;
  /** Heading override. Blank uses the form's own title. Localizable. */
  title?: string | Record<string, string>;
  /** Intro copy override. Blank uses the form's own description. Localizable. */
  description?: string | Record<string, string>;
  /** Submit button label. Localizable. */
  submitLabel?: string | Record<string, string>;
  /** Shown after a successful submit. Localizable. */
  successText?: string | Record<string, string>;
  /** Shown when the viewer already submitted a one-per-person form. Localizable. */
  alreadySubmittedText?: string | Record<string, string>;
  /**
   * Auth-gate the block. 'any' (default) always renders; 'in' only for
   * signed-in users; 'out' only for anonymous visitors.
   */
  requireAuth?: 'any' | 'in' | 'out';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/** What `GET /api/forms/{form}` returns. */
interface FormPayload {
  id: number;
  slug?: string;
  name?: string;
  singleton?: boolean;
  editable?: boolean;
  /** Times the signed-in viewer has already submitted. Absent when anonymous. */
  submitted?: number;
  /** What the viewer last saved. Sent only for an editable singleton. */
  values?: Record<string, unknown>;
  form?: unknown;
}

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');

/** Anti-spam metadata the submit route checks and then strips. Names must
 *  match the constants in `app/api/(lms)/forms/[form]/submit/route.ts`. */
const HONEYPOT_FIELD = '_hp';
const ELAPSED_FIELD = '_elapsedMs';

export function DynamicFormBlock({
  form,
  title,
  description,
  submitLabel,
  successText,
  alreadySubmittedText,
  requireAuth,
  paddingOuter,
  paddingInner,
}: DynamicFormBlockProps) {
  const { t } = useTranslation('common');
  const currentUser = useCurrentUser();

  const [payload, setPayload] = useState<FormPayload | null>(null);
  const [definition, setDefinition] = useState<FormDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  /** When the form became fillable — the submit route rejects anything
   *  returned faster than a person could have read it. */
  const shownAt = useRef<number>(0);

  const key = (form ?? '').trim();
  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  const hidden = (gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn);

  /** What the operator typed on this block wins; then the translation
   *  catalogue; then a hard-coded last resort. The catalogue can't come first
   *  — a tenant that translates `form.success` would silently override the
   *  message an operator wrote for one specific form. i18next echoes the key
   *  back when it has no entry, hence the identity check. */
  function label(k: string, prop: string | Record<string, string> | undefined, fallback: string) {
    const override = tx(prop);
    if (override) return override;
    const translated = t(k);
    return translated && translated !== k ? translated : fallback;
  }

  useEffect(() => {
    if (!key || hidden) {
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setLoadError(false);
    setSubmitted(false);
    setSubmitError(null);

    fetch(`${BASE}/api/forms/${encodeURIComponent(key)}`, {
      headers: { Accept: 'application/json' },
      signal: ctrl.signal,
    })
      .then(async (res) => (res.ok ? ((await res.json()) as FormPayload) : null))
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const parsed = asDefinition(data?.form);
        if (!data || !parsed) {
          setLoadError(true);
          setPayload(null);
          setDefinition(null);
        } else {
          setPayload(data);
          setDefinition(parsed);
          shownAt.current = Date.now();
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setLoadError(true);
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [key, hidden]);

  async function submit(values: Record<string, unknown>) {
    if (!payload) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(
        `${BASE}/api/forms/${encodeURIComponent(String(payload.id))}/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            ...values,
            [ELAPSED_FIELD]: shownAt.current ? Date.now() - shownAt.current : 0,
          }),
        },
      );
      if (res.ok) {
        setSubmitted(true);
        return;
      }
      // 409 is the backend's answer to a repeat submission of a one-per-person
      // form (it raises ALREADY_SUBMITTED as a conflict); it is the only
      // conflict this endpoint can produce. Keying on the status rather than
      // on the message text keeps this working if the wording is translated.
      setSubmitError(
        res.status === 409
          ? label('form.alreadySubmitted', alreadySubmittedText, 'Bu formu zaten doldurdunuz.')
          : label('form.submitError', undefined, 'Form gönderilemedi. Lütfen tekrar deneyin.'),
      );
    } catch {
      setSubmitError(
        label('form.submitError', undefined, 'Form gönderilemedi. Lütfen tekrar deneyin.'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (hidden) return null;

  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);
  const heading = tx(title) || definition?.title || payload?.name || '';
  const intro = tx(description) || definition?.description || '';
  const alreadyFilled = Boolean(payload?.singleton) && (payload?.submitted ?? 0) > 0;

  const validationLabels: ValidationLabels = {
    required: label('form.required', undefined, 'Bu alan zorunludur.'),
    tooShort: (n) => label('form.tooShort', undefined, `En az ${n} karakter giriniz.`),
    tooLong: (n) => label('form.tooLong', undefined, `En fazla ${n} karakter giriniz.`),
    tooSmall: (n) => label('form.tooSmall', undefined, `En küçük değer ${n}.`),
    tooLarge: (n) => label('form.tooLarge', undefined, `En büyük değer ${n}.`),
    pattern: label('form.pattern', undefined, 'Geçerli bir değer giriniz.'),
  };

  return (
    <BlockRoot as="div" style={outerPad}>
      <div style={{ ...sectionInner, ...innerPad }}>
        {heading ? <h2 style={headingStyle}>{heading}</h2> : null}
        {intro ? <p style={introStyle}>{intro}</p> : null}

        {!key ? (
          <p style={messageStyle}>{label('form.notConfigured', undefined, 'Form seçilmedi.')}</p>
        ) : loading ? (
          <div style={skeletonWrapStyle}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={skeletonRowStyle} />
            ))}
          </div>
        ) : loadError || !definition ? (
          <p style={messageStyle}>{label('form.loadError', undefined, 'Form yüklenemedi.')}</p>
        ) : submitted ? (
          <p style={successStyle}>
            {label('form.success', successText, 'Formunuz alınmıştır. Teşekkür ederiz.')}
          </p>
        ) : alreadyFilled && !payload?.editable ? (
          <p style={successStyle}>
            {label('form.alreadySubmitted', alreadySubmittedText, 'Bu formu zaten doldurdunuz.')}
          </p>
        ) : (
          <>
            {submitError ? <p style={errorStyle}>{submitError}</p> : null}
            <DynamicFormRenderer
              // Remounts when the operator points the block at another form:
              // the renderer holds the answers in state, and without a new key
              // React would keep the previous form's values alive under it.
              key={payload?.id ?? key}
              definition={definition}
              initialValues={payload?.values}
              honeypotName={HONEYPOT_FIELD}
              onSubmit={submit}
              submitting={submitting}
              submitLabel={label('form.submit', submitLabel, 'Gönder')}
              labels={validationLabels}
            />
          </>
        )}
      </div>
    </BlockRoot>
  );
}

/**
 * Read a stored document as a designer form.
 *
 * <p>Shape-checked rather than trusted: the same column holds forms written by
 * the legacy builder ({@code {layout, columns, fields}}), and rendering one of
 * those as if it had sections would silently produce an empty form.
 */
function asDefinition(raw: unknown): FormDefinition | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const doc = raw as Record<string, unknown>;
  if (!Array.isArray(doc.sections)) return null;
  return doc as unknown as FormDefinition;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const headingStyle: CSSProperties = {
  margin: '0 0 8px',
  fontSize: 'var(--font-size-2xl, 24px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
};

const introStyle: CSSProperties = {
  margin: '0 0 20px',
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const messageStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #6b7280)',
  padding: 'var(--space-8, 32px) 0',
  textAlign: 'center',
};

const successStyle: CSSProperties = {
  padding: '14px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'color-mix(in srgb, var(--color-success-500, #10b981) 14%, transparent)',
  color: 'var(--color-success-600, #059669)',
  fontSize: 'var(--font-size-sm, 14px)',
};

const errorStyle: CSSProperties = {
  marginBottom: 12,
  padding: '10px 14px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'color-mix(in srgb, var(--color-danger-500, #ef4444) 12%, transparent)',
  color: 'var(--color-danger-600, #dc2626)',
  fontSize: 'var(--font-size-sm, 13px)',
};

const skeletonWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const skeletonRowStyle: CSSProperties = {
  height: 46,
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f4f4f5)',
  opacity: 0.6,
};
